import { apiFetch } from "./client";

// Forma della vista (backend/app/preventivo/vista.py). Gli importi arrivano come stringhe
// decimali con segno dal lato del cliente: "-28.57" = paga meno.
export interface OffertaMenu {
  id: string;
  nome: string;
  fornitore: string;
  quota: string | null;
  spread: string | null;
  /** Fine di validità (ISO); `null` se la CTE non ne ha una: resta in vendita. */
  valida_fino: string | null;
  /** «2026-11-02»: l'offerta scelta non è più in vendita. Non è una voce del menù: resta solo accanto alla scelta. */
  scaduta_il?: string;
}

/** Spread stampato in bolletta: una riga «tutte», oppure una per fascia. */
export interface SpreadFascia {
  fasce: "tutte" | "F1F2" | "F3";
  valore: string;
}

/** Un altro addebito del venditore: `euro` null quando la bolletta non dà l'importo. */
export interface AltroAddebito {
  nome: string;
  euro: string | null;
  /** La quota potenza fatturata su più kW dell'impegnata: gli € sono in più al mese («+2,14 € al mese»). */
  al_mese?: boolean;
  /** Solleciti, insoluto, mora: la pagina scrive sotto «fuori dal confronto». Assente nelle viste di prima del 23/10. */
  fuori_confronto?: boolean;
}

export interface BollettaVista {
  id: string;
  stato: string;
  /** false finché l'anagrafica (CF, POD, intestatario) non è stata provata: la pagina continua a chiedere. */
  anagrafica_letta: boolean;
  mese_label: string | null;
  periodo_mesi: number | null;
  pcv: string | null;
  spread: SpreadFascia[] | null;
  spread_nota: string | null;
  /** Lo spread senza valore: «non_dichiarato» (il riquadro non lo scrive), «non_letto» (scritto e non ottenuto); `null` a prezzo fisso. */
  spread_stato: "dichiarato" | "non_dichiarato" | "non_letto" | null;
  /** Con «non_dichiarato»: il ricarico per kWh già calcolato dal conto, come «0.090». */
  spread_calcolato: string | null;
  ricarico: string | null;
  ricarico_kwh: string | null;
  ricarico_dove: string | null;
  /** Capacità a due prezzi senza i kWh delle ore di picco: l'importo è un minimo, la pagina scrive «almeno». */
  ricarico_minimo: boolean;
  /** PUN ora per ora su un profilo da condominio: l'importo è una stima, la pagina scrive il solo numero (Alfonso 29/10). */
  ricarico_stima: boolean;
  /** «assente»: il conto torna (la pagina scrive «—»); «non_calcolabile»: il conto non si è potuto fare (cella vuota). */
  ricarico_stato: "assente" | "non_calcolabile" | "presente";
  altri: AltroAddebito[];
  dettaglio: "pronto" | "in_corso" | "non_leggibile";
  diff_mese_per_offerta: Record<string, string>;
  /** Bolletta incompleta (`stato` «non_letta» dentro il condominio): il dato che manca, senza differenza. */
  motivo: string | null;
  /** Foto o immagine, non un PDF: solo a queste «Aggiungi pagina» può servire. */
  foto: boolean;
  /** Senza la potenza impegnata, rete e oneri sono quelli della bolletta sui due lati: la differenza c'è, la pagina lo dice. */
  rete_dalla_bolletta: boolean;
  /** Solo sulla riga gas (giro 1 del gas): senza confronto con le offerte, `diff_mese_per_offerta` vuoto. */
  commodity?: "gas";
  /** L'analisi dell'AI sul gas: fatta, mancano i valori ufficiali del mese (`valori_del`), non riuscita; `null` finché la fase 2 non è finita. */
  analisi_gas?: "fatta" | "valori_mancanti" | "non_riuscita" | null;
  /** «2026-10»: il mese dei valori ufficiali che mancano (l'ultimo mese del periodo). */
  valori_del?: string | null;
}

export interface Contatore {
  chiave: string;
  pod: string | null;
  fornitura: string;
  offerta_id: string | null;
  propria: boolean;
  mese: string | null;
  diff_per_offerta: Record<string, string>;
  bollette: BollettaVista[];
}

/** Un contatore gas (PDR): vive nella chiave `contatori_gas` del condominio, mai fra i contatori luce. */
export interface ContatoreGas {
  chiave: string;
  pdr: string;
  /** «centrale termica, G40»; `null` senza palazzina né classe letta. */
  fornitura: string | null;
  bollette: BollettaVista[];
}

/** Una frase dell'esito, per bolletta: `fornitura` è l'etichetta della colonna «Fornitura», `null` se il condominio ha
 *  una bolletta sola; `bolletta_id` la bolletta della riga. */
export interface VoceEsito {
  fornitura: string | null;
  /** La bolletta della voce: la tabella la apre sotto quella riga (spec § 7). */
  bolletta_id: string;
  frase: string;
}

/** Un esito per titolo: «G», «P», «N», «V» o «da_verificare», con una frase per bolletta. */
export interface Esito {
  giudizio: string;
  titolo: string;
  voci: VoceEsito[];
}

export interface Condominio {
  chiave: string;
  titolo: string;
  venditore: string | null;
  offerta_attuale: string | null;
  n_forniture: number;
  offerta_id: string | null;
  mese: string | null;
  anno: string | null;
  contatori_contati: number;
  contatori_totali: number;
  diff_per_offerta: Record<string, string>;
  esiti: Esito[];
  contatori: Contatore[];
  /** Solo con l'interruttore del gas acceso e almeno una bolletta gas nel condominio (giro 1 del gas): senza, le chiavi non ci sono. */
  venditore_gas?: string | null;
  contatori_gas?: ContatoreGas[];
  esiti_gas?: Esito[];
}

export interface BollettaFuori {
  id: string;
  /** `null` per le pagine fotografate col telefono: la pagina scrive «Foto · N pagine». */
  nome_file: string | null;
  stato: "in_lettura" | "in_coda" | "non_letta" | "gas";
  motivo: string | null;
  anagrafica_letta: boolean;
  pagine: number;
}

/** Chi riceve il preventivo: tutti i campi facoltativi, `null` se non scritti. */
export interface Amministratore {
  nome: string | null;
  email: string | null;
  cellulare: string | null;
}

/** Un invio all'amministratore; `scaduto` lo calcola il server, il registro resta anche dopo la scadenza del link. */
export interface Invio {
  canale: "mail" | "whatsapp";
  destinatario: string | null;
  il: string;
  scade_il: string;
  scaduto: boolean;
}

/** Una riga della lista dei preventivi salvati (`GET /preventivo/elenco`). */
export interface VoceElenco {
  id: string;
  amministratore: string | null;
  condomini: number;
  forniture: number;
  bollette: number;
  /** I primi due titoli: al posto dei numeri quando l'amministratore non ha un nome. */
  primi_condomini: string[];
  /** Totale «Tutti i condomìni», con segno; `null` se nessuna offerta è scelta. */
  differenza_mese: string | null;
  modificato_il: string;
  ultimo_invio: { canale: "mail" | "whatsapp"; il: string } | null;
}

/** L'ultimo preventivo toccato dall'utente, in breve (`GET /preventivo/ultimo`). */
export interface Ultimo {
  id: string;
  amministratore: string | null;
  /** Quante bollette ha. */
  totale: number;
  condomini: string[];
  ultimo_caricamento: string | null;
}

export interface Vista {
  /** Id, amministratore e invii: solo nella vista dell'operatore (la copia per l'amministratore non li ha). */
  id?: string;
  amministratore?: Amministratore;
  invii?: Invio[];
  lette: number;
  totale: number;
  offerte: OffertaMenu[];
  condomini: Condominio[];
  bollette_fuori: BollettaFuori[];
  /** Bollette al massimo per preventivo (`MAX_BOLLETTE` del server). Assente nella vista del link per l'amministratore. */
  massimo_bollette?: number;
  /** Istante (ISO 8601) dell'ultima bolletta caricata nel preventivo aperto; `null` se vuoto. Solo nella vista dell'operatore. */
  ultimo_caricamento?: string | null;
}

const BASE = "/preventivo";

function conFile(file: File): FormData {
  const form = new FormData();
  form.append("file", file);
  return form;
}

/** Ogni scrittura dice su quale preventivo agisce (spec § 6): nessun «preventivo corrente» dedotto dal server. */
export const preventivoApi = {
  /** `link`: l'interruttore del solo link per l'amministratore (giro 5); assente nelle risposte vecchie = spento. */
  stato: () => apiFetch<{ attivo: boolean; link?: boolean; gas?: boolean }>(`${BASE}/stato`),
  elenco: (q?: string) => apiFetch<VoceElenco[]>(`${BASE}/elenco${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  /** 204 = l'utente non ne ha ancora: `null`. */
  ultimo: async (): Promise<Ultimo | null> => (await apiFetch<Ultimo | undefined>(`${BASE}/ultimo`)) ?? null,
  /** «Nuovo preventivo»: niente si chiude; un preventivo vuoto dell'utente si riusa. */
  nuovo: async (): Promise<{ id: string }> => {
    const r = await apiFetch<{ id: string }>(`${BASE}/nuovo`, { method: "POST" });
    return { id: r.id };
  },
  vista: (pid: string) => apiFetch<Vista>(`${BASE}/${pid}`),
  eliminaPreventivo: async (pid: string): Promise<void> => {
    await apiFetch<unknown>(`${BASE}/${pid}`, { method: "DELETE" });
  },
  /** Campo assente = non si tocca; stringa vuota = si cancella. 422: `email_non_valida` / `cellulare_non_valido`. */
  amministratore: (pid: string, campi: { nome?: string; email?: string; cellulare?: string }) =>
    apiFetch<Vista>(`${BASE}/${pid}/amministratore`, { method: "PUT", body: JSON.stringify(campi) }),
  carica: async (pid: string, file: File): Promise<{ id: string }> => {
    const r = await apiFetch<{ id: string }>(`${BASE}/${pid}/bollette`, { method: "POST", body: conFile(file) });
    return { id: r.id };
  },
  /** «Bolletta finita»: le pagine fotografate, nell'ordine, come UNA bolletta. `idInvio` lo genera il telefono: lo
   *  stesso invio ripetuto non crea una seconda bolletta. */
  caricaFoto: async (pid: string, pagine: Blob[], idInvio?: string, sostituisce?: string): Promise<{ id: string }> => {
    const form = new FormData();
    if (idInvio) form.append("id_invio", idInvio);
    if (sostituisce) form.append("sostituisce", sostituisce);   // «Rifai la foto»: al tetto la vecchia non conta
    pagine.forEach((p, i) => form.append("files", p, `pagina-${i + 1}.jpg`));
    const r = await apiFetch<{ id: string }>(`${BASE}/${pid}/bollette/foto`, { method: "POST", body: form });
    return { id: r.id };
  },
  aggiungiPagina: async (pid: string, bid: string, file: File): Promise<void> => {
    await apiFetch<unknown>(`${BASE}/${pid}/bollette/${bid}/pagine`, { method: "POST", body: conFile(file) });
  },
  elimina: async (pid: string, bid: string): Promise<void> => {
    await apiFetch<unknown>(`${BASE}/${pid}/bollette/${bid}`, { method: "DELETE" });
  },
  scegliCondominio: (pid: string, chiave: string, offertaId: string) =>
    apiFetch<Vista>(`${BASE}/${pid}/scelte`, {
      method: "PUT",
      body: JSON.stringify({ condominio: chiave, offerta_id: offertaId }),
    }),
  scegliContatore: (pid: string, chiave: string, offertaId: string | null) =>
    apiFetch<Vista>(`${BASE}/${pid}/scelte`, {
      method: "PUT",
      body: JSON.stringify({ contatore: chiave, offerta_id: offertaId }),
    }),
  /** «Stessa offerta per tutti»: l'offerta su ogni condominio, in un salvataggio solo (le forniture con un'offerta
   *  propria restano come sono). */
  scegliTutti: (pid: string, offertaId: string) =>
    apiFetch<Vista>(`${BASE}/${pid}/scelte/tutti`, { method: "PUT", body: JSON.stringify({ offerta_id: offertaId }) }),
  fileUrl: (pid: string, bid: string) => `/api${BASE}/${pid}/bollette/${bid}/file`,
  /** Link e mail in un colpo: risponde la vista con l'invio nuovo. 409 `email_mancante`, 502 `mail_non_partita`,
   *  403 `link_non_attivo`. */
  /** `email` è l'indirizzo che la pagina mostra sotto il tasto: se nel frattempo è cambiato il server risponde 409
   *  `email_cambiata` e non manda nulla. */
  mandaMail: (pid: string, condomini: string[], email: string) =>
    apiFetch<Vista>(`${BASE}/${pid}/invia/mail`, { method: "POST", body: JSON.stringify({ condomini, email }) }),
  /** Crea il link e registra l'invio: la pagina apre WhatsApp col `token`. */
  whatsapp: (pid: string, condomini: string[]) =>
    apiFetch<{ token: string; scade_il: string; vista: Vista }>(`${BASE}/${pid}/invia/whatsapp`, {
      method: "POST", body: JSON.stringify({ condomini }),
    }),
};
